import React, { useState, useEffect } from 'react';
import { PageWrap, PageHeader } from '../components/UI';
import { apiFetch, apiPost } from '../api';
import usePlans, { getEffectivePrice, getSavingPct } from '../hooks/usePlans';
import { getTierLabel } from '../hooks/pricingData';
import '../styles/Subscription.css';

function toPlanCard(plan, cycle, unitCount) {
  const price = plan.priceMonthly > 0 ? getEffectivePrice(plan, cycle, unitCount) : 0;
  const maxC = plan.maxClientsActivated;
  return {
    code: plan.planCode,
    name: plan.name,
    price,
    isFree: plan.priceMonthly === 0,
    unit: plan.durationUnit,
    limit: maxC == null
      ? 'Unlimited clients'
      : `Up to ${maxC} client${maxC !== 1 ? 's' : ''}`,
    badge: plan.featured ? 'Popular' : null,
    features: plan.ruleList,
    missing: plan.ruleListDisabled,
    featured: plan.featured,
    savePct: plan.priceMonthly > 0 ? getSavingPct(plan, unitCount) : 0,
    trialDays: plan.trialDays || 30,
    bulkThreshold: plan.bulkThreshold,
  };
}

const BADGE_COLORS = {
  'Popular':   { bg: '#E0F2FE', color: '#0369A1' },
  'Best Value':{ bg: '#D1FAE5', color: '#065F46' },
  'AI Powered':{ bg: '#EDE9FE', color: '#5B21B6' },
};

function PlanCard({ plan, isActive, onSelect, loading }) {
  const badgeStyle = plan.badge ? BADGE_COLORS[plan.badge] : null;
  return (
    <div
      className="sub-plan-card"
      style={{
        border: `2px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
        background: isActive ? 'rgba(79,142,247,0.04)' : 'var(--bg2)',
      }}
    >
      {plan.badge && (
        <div
          className="sub-plan-badge"
          style={{ background: badgeStyle.bg, color: badgeStyle.color }}
        >
          {plan.badge}
        </div>
      )}

      <div>
        <div className="sub-plan-name">{plan.name}</div>
        <div className="sub-price-row">
          <span className={`sub-price-amount${plan.isFree ? ' sub-price-amount--free' : ''}`}>
            {plan.isFree ? 'Free' : `₹${plan.price.toLocaleString('en-IN')}`}
          </span>
          {!plan.isFree && <span className="sub-price-unit">/{plan.unit}</span>}
        </div>
        <div className="sub-plan-limit">{plan.limit}</div>
      </div>

      <div className="sub-features-list">
        {plan.features.map(f => (
          <div key={f} className="sub-feature-row">
            <span className="sub-feature-check">✓</span>
            <span className="sub-feature-text">{f}</span>
          </div>
        ))}
        {plan.missing.map(f => (
          <div key={f} className="sub-feature-row">
            <span className="sub-missing-dash">—</span>
            <span className="sub-missing-text">{f}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => onSelect(plan.code)}
        disabled={isActive || loading}
        className="sub-btn-select"
        style={{
          background: isActive ? 'var(--accent)' : 'transparent',
          color:      isActive ? '#fff' : 'var(--accent)',
        }}
      >
        {isActive ? '✓ Current Plan' : loading ? 'Updating…' : 'Select Plan'}
      </button>
    </div>
  );
}

function getAllowedPlanType(userRole) {
  if (userRole?.isCA || userRole?.isAdmin) return 'ca';
  if (userRole?.isCustomer) return 'client';
  return 'ca';
}

export default function Subscription({ userRole, onRoleUpgrade }) {
  const { caPlans, clientPlans, loading: plansLoading, error: plansError } = usePlans();

  const [currentSub, setCurrentSub] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [success,    setSuccess]    = useState(null);
  const [billing,    setBilling]    = useState('annual');
  const [unitCount,  setUnitCount]  = useState(5);

  const allowedPlanType = getAllowedPlanType(userRole);
  const [tab, setTab] = useState(allowedPlanType);

  useEffect(() => {
    apiFetch('/subscriptions/me')
      .then(data => setCurrentSub(data))
      .catch(() => setError('Failed to load subscription'))
      .finally(() => setSubLoading(false));
  }, []);

  const handleSelect = async (planCode) => {
    if (planCode === currentSub?.plan_code) return;
    setSaving(true); setError(null); setSuccess(null);
    try {
      await apiPost('/subscriptions/me', { plan_code: planCode, unit_count: unitCount, billing_cycle: billing });
      const updated = await apiFetch('/subscriptions/me');
      setCurrentSub(updated);
      setSuccess('Plan updated successfully!');
      setTimeout(() => setSuccess(null), 4000);
      onRoleUpgrade?.({ subscriptionPlan: planCode });
    } catch (err) {
      setError(err.message || 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  };

  const rawPlans = tab === 'ca' ? caPlans : clientPlans;
  const plans    = rawPlans.map(p => toPlanCard(p, billing, unitCount));
  const loading  = plansLoading || subLoading;

  return (
    <PageWrap>
      <PageHeader title="Subscription" sub="Choose the plan that fits your practice" />

      {currentSub && (
        <div className="sub-current-banner">
          <div>
            <div className="sub-current-label">Current Plan</div>
            <div className="sub-current-plan-name">
              {currentSub.plan_name || currentSub.plan_code}
              {currentSub.max_units && (
                <span className="sub-units-used">
                  {currentSub.current}/{currentSub.max_units} units used
                </span>
              )}
            </div>
          </div>
          <span className="sub-active-badge">Active</span>
        </div>
      )}

      {userRole?.isAdmin && (
        <div className="sub-tab-switcher">
          {[['ca', 'CA Plans'], ['client', 'Client Plans']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`sub-tab-btn${tab === key ? ' sub-tab-btn--active' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {!userRole?.isTeamMember && (
        <div className="sub-billing-wrap">
          <div className="sub-billing-toggle">
            {[['monthly', 'Monthly'], ['annual', 'Annual']].map(([c, label]) => (
              <button
                key={c}
                onClick={() => setBilling(c)}
                className={`sub-billing-btn${billing === c ? ' sub-billing-btn--active' : ''}`}
              >
                {label}
                {c === 'annual' && <span className="sub-annual-note">2 mo free</span>}
              </button>
            ))}
          </div>
          {tab === 'ca' && (
            <div className="sub-seats-wrap">
              <span className="sub-seats-label">CA client seats:</span>
              <input
                type="number"
                min={5}
                value={unitCount}
                onChange={e => setUnitCount(Math.max(5, parseInt(e.target.value) || 5))}
                className="sub-seats-input"
              />
              {unitCount > 50 && <span className="sub-bulk-rate">Bulk rate applied</span>}
            </div>
          )}
        </div>
      )}

      <p className="sub-plan-desc">
        {allowedPlanType === 'ca'
          ? 'For Chartered Accountants managing multiple clients. Includes client portal sharing and full team access.'
          : 'For businesses and in-house accountants managing their own Tally data. No CA portal sharing.'}
      </p>

      {userRole?.isTeamMember && (
        <div className="sub-team-notice">
          Subscription is managed by your CA firm. Contact your CA to change the plan.
        </div>
      )}

      {(error || plansError) && (
        <div className="sub-alert sub-alert--error">{error || plansError}</div>
      )}
      {success && (
        <div className="sub-alert sub-alert--success">{success}</div>
      )}

      {loading ? (
        <div className="sub-loading">Loading plans…</div>
      ) : (
        <div className="sub-plans-grid">
          {plans.map(plan => (
            <PlanCard
              key={plan.code}
              plan={plan}
              isActive={currentSub?.plan_code === plan.code}
              onSelect={handleSelect}
              loading={saving || userRole?.isTeamMember}
            />
          ))}
        </div>
      )}

      <p className="sub-footnote">
        * All plans include a 30-day free trial — no credit card required. CA plans: 20% volume discount above 50 seats (min 5 seats). Billing integration coming soon.
      </p>
    </PageWrap>
  );
}
